// src/components/hero/EngineStoryLine.jsx — Phase 2 / Change E
// Canonical Explainability Block — "Why VIRAJ Acted"
// Format: reasons[] + Action + Confidence + Mode + Attribution + Override Sensitivity
// Backward compat: accepts string story for legacy callers.

export default function EngineStoryLine({ story, riskStatus, engineOnline, decision }) {
  if (!engineOnline) return null;
  const isHigh = riskStatus === "high" || riskStatus === "critical";

  // Change E: canonical explainability block
  if (decision && Array.isArray(decision.reasons) && decision.reasons.length > 0) {
    const conf = typeof decision.confidence === "number" ? decision.confidence : null;
    const confInfo = conf == null ? null : conf >= 0.75 ? { label:"HIGH", color:"#12cc76" }
                    : conf >= 0.5 ? { label:"MEDIUM", color:"#f5a623" } : { label:"LOW", color:"#e85555" };
    const actionColors = { reduce:"var(--warning)", pause:"var(--danger)",
                           alert:"var(--warning)", observe:"var(--action)", none:"var(--success)" };
    const actionColor = actionColors[decision.action] ?? "var(--text-secondary)";

    return (
      <div style={{
        padding:"14px 18px", borderRadius:"var(--radius)", maxWidth:480, width:"100%",
        background: isHigh ? "var(--danger-bg)" : "var(--bg-card)",
        border:`1px solid ${isHigh ? "var(--danger-border)" : "var(--border-subtle)"}`,
      }}>
        <div style={{ fontFamily:"var(--font-mono)", fontSize:9, letterSpacing:"0.14em",
                      textTransform:"uppercase", color:"var(--text-muted)", marginBottom:10 }}>
          Why VIRAJ Acted
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:4, marginBottom:10 }}>
          {decision.reasons.map((r,i) => (
            <div key={i} style={{ display:"flex", gap:6, alignItems:"flex-start" }}>
              <span style={{ color:"var(--danger)", flexShrink:0, marginTop:1 }}>↓</span>
              <span style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--text-secondary)", lineHeight:1.5 }}>{r}</span>
            </div>
          ))}
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(86px,1fr))",
                      gap:10, paddingTop:8, borderTop:"1px solid var(--border-subtle)" }}>
          <div>
            <div style={{ fontFamily:"var(--font-mono)", fontSize:8, color:"var(--text-muted)", marginBottom:2 }}>Action</div>
            <div style={{ fontFamily:"var(--font-mono)", fontSize:12, fontWeight:700, color:actionColor, textTransform:"uppercase" }}>
              {decision.action ?? "observe"}
            </div>
          </div>
          {confInfo && (
            <div>
              <div style={{ fontFamily:"var(--font-mono)", fontSize:8, color:"var(--text-muted)", marginBottom:2 }}>Confidence</div>
              <div style={{ fontFamily:"var(--font-mono)", fontSize:12, fontWeight:700, color:confInfo.color }}>
                {conf.toFixed(2)} ({confInfo.label})
              </div>
            </div>
          )}
          {decision.mode && (
            <div>
              <div style={{ fontFamily:"var(--font-mono)", fontSize:8, color:"var(--text-muted)", marginBottom:2 }}>Mode</div>
              <div style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--text-secondary)", textTransform:"capitalize" }}>{decision.mode}</div>
            </div>
          )}
          {decision.attribution_state && (
            <div>
              <div style={{ fontFamily:"var(--font-mono)", fontSize:8, color:"var(--text-muted)", marginBottom:2 }}>Attribution</div>
              <div style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--text-secondary)" }}>{decision.attribution_state}</div>
            </div>
          )}
          {decision.override_sensitivity != null && (
            <div>
              <div style={{ fontFamily:"var(--font-mono)", fontSize:8, color:"var(--text-muted)", marginBottom:2 }}>Override Sens.</div>
              <div style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"#12cc76", fontWeight:700 }}>{decision.override_sensitivity}</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Backwards compat: string story
  if (!story) return null;
  return (
    <div style={{
      padding:"10px 14px", borderRadius:"var(--radius)", fontFamily:"var(--font-mono)",
      fontSize:11, letterSpacing:"0.03em", lineHeight:1.5,
      background: isHigh ? "var(--danger-bg)" : "var(--bg-subtle)",
      border:`1px solid ${isHigh ? "var(--danger-border)" : "var(--border-subtle)"}`,
      color: isHigh ? "var(--danger)" : "var(--text-tertiary)",
    }}>
      {story}
    </div>
  );
}
